import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { getApiErrorMessage } from '@helpers/getApiErrorMessage';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AsrModelSettingsService } from 'app/API/custom/asr-model-settings/AsrModelSettingsService';
import type { TAsrModelSettings } from 'app/API/custom/asr-model-settings/types';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { useState } from 'react';
import type { TProps } from './types/TProps';

export const ModelNormalizerSettings = ({ model }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string[] | null>(null);
  const enabledNames =
    draft ?? model.normalizers.filter((normalizer) => normalizer.enabled).map((normalizer) => normalizer.name);
  const hasChanges = model.normalizers.some(
    (normalizer) => enabledNames.includes(normalizer.name) !== normalizer.enabled,
  );
  const mutation = useMutation({
    mutationFn: (names: string[]) =>
      call(() => AsrModelSettingsService.setModelNormalizers(model.modelName, names), { dontRedirectOnError: true }),
    onMutate: () => queryClient.cancelQueries({ queryKey: asrProxyKeys.modelSettings() }),
    onSuccess: async (saved) => {
      await queryClient.cancelQueries({ queryKey: asrProxyKeys.modelSettings() });
      queryClient.setQueryData<TAsrModelSettings[]>(asrProxyKeys.modelSettings(), (models) =>
        models?.map((item) => (item.modelName === saved.modelName ? saved : item)),
      );
      setDraft(null);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: asrProxyKeys.modelSettings() }),
  });

  return (
    <article className="rounded-md border border-border bg-page p-4">
      <h3 className="font-medium text-foreground">{model.modelName}</h3>
      <p className="mt-1 text-sm text-foreground-muted">
        {model.provider} · {model.providerModel}
      </p>
      <div className="mt-4 space-y-4">
        {model.normalizers.map((normalizer) => (
          <div key={normalizer.name} className="rounded-md border border-border bg-surface p-4">
            <Switch
              label={normalizer.name}
              checked={enabledNames.includes(normalizer.name)}
              disabled={mutation.isPending}
              onChange={() => {
                mutation.reset();
                setDraft(
                  enabledNames.includes(normalizer.name)
                    ? enabledNames.filter((name) => name !== normalizer.name)
                    : [...enabledNames, normalizer.name],
                );
              }}
            />
            <p className="mt-2 text-sm text-foreground-muted">{normalizer.description}</p>
          </div>
        ))}
        {model.normalizers.length === 0 && (
          <p className="text-sm text-foreground-muted">No normalizers are registered for this model.</p>
        )}
      </div>
      {mutation.isError && (
        <p role="alert" className="mt-3 text-sm text-danger">
          {getApiErrorMessage(mutation.error) ||
            'Could not save model settings. Your changes are still available to retry.'}
        </p>
      )}
      {mutation.isSuccess && !hasChanges && (
        <p role="status" className="mt-3 text-sm text-foreground-muted">
          Model settings saved.
        </p>
      )}
      <div className="mt-4 flex gap-2">
        <Button
          className="!w-auto"
          disabled={!hasChanges || mutation.isPending}
          onClick={() => mutation.mutate(enabledNames)}
        >
          {mutation.isPending ? 'Saving…' : 'Save normalizers'}
        </Button>
        <OutlinedButton
          className="!w-auto"
          disabled={draft === null || mutation.isPending}
          onClick={() => {
            setDraft(null);
            mutation.reset();
          }}
        >
          Discard changes
        </OutlinedButton>
      </div>
    </article>
  );
};
